import { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useNotify } from 'react-admin';

import {
    ContractorNetworkRankings as RankingsPayload,
    ContractorRankingEntry,
    fetchContractorNetworkRankings,
} from '@/utils/contractor_owner_utils';

const CARD_SHADOW = '0 4px 12px rgba(0,0,0,0.05)';
const CARD_SHADOW_HOVER = '0 8px 20px rgba(0,0,0,0.08)';

// Copy of DashboardPage softCardSx chrome (do not extract shared util for v1).
const softCardSx = {
    borderRadius: '16px',
    boxShadow: CARD_SHADOW,
    border: 'none',
    bgcolor: '#fff',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: CARD_SHADOW_HOVER,
    },
};

const ACCENT_PROPERTIES = '#3B82F6';
const ACCENT_OWNERS = '#10B981';

function RankingCard({
    title,
    accent,
    rows,
}: {
    title: string;
    accent: string;
    rows: ContractorRankingEntry[];
}) {
    return (
        <Card
            elevation={0}
            sx={{
                ...softCardSx,
                flex: 1,
                minWidth: 220,
                borderLeft: `4px solid ${accent}`,
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                    variant="subtitle2"
                    sx={{ color: '#6B7280', fontWeight: 600, mb: 1.5 }}
                >
                    {title}
                </Typography>
                {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No data yet
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {rows.map((row, index) => (
                            <Stack
                                key={row.id}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                            >
                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                    <Box
                                        component="span"
                                        sx={{ color: '#9CA3AF', mr: 1 }}
                                    >
                                        {index + 1}.
                                    </Box>
                                    <MuiLink
                                        component={Link}
                                        to={`/contractor_owners/${row.id}/show`}
                                        underline="hover"
                                        color="inherit"
                                    >
                                        {row.name}
                                    </MuiLink>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: accent }}
                                >
                                    {row.count}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

export const ContractorNetworkRankings = () => {
    const notify = useNotify();
    const [rankings, setRankings] = useState<RankingsPayload | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchContractorNetworkRankings()
            .then(data => {
                if (!cancelled) setRankings(data);
            })
            .catch(err => {
                if (!cancelled) {
                    notify(
                        err instanceof Error
                            ? err.message
                            : 'Failed to load contractor rankings',
                        { type: 'error' },
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, [notify]);

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={5}
            sx={{ pt: 2, mb: 2, width: '100%' }}
        >
            <RankingCard
                title="Top contractors by properties"
                accent={ACCENT_PROPERTIES}
                rows={rankings?.by_properties ?? []}
            />
            <RankingCard
                title="Top contractors by owners"
                accent={ACCENT_OWNERS}
                rows={rankings?.by_owners ?? []}
            />
        </Stack>
    );
};
